/**
 * Deactivate Account (soft-delete · 30-day window · Aug 2026 mobile parity).
 * Hard-delete lives on this page too under "Delete permanently" — irreversible.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, PauseCircle, Trash2, Loader2, AlertTriangle } from 'lucide-react';

export default function DeactivateAccount() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [deactivating, setDeactivating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState('');

  const doDeactivate = async () => {
    if (!window.confirm("Pause your account for 30 days? Sign back in any time to reactivate.")) return;
    setDeactivating(true);
    try {
      const r = await api.post('/me/deactivate');
      toast.success(`Account paused until ${new Date(r.data.reactivate_by).toLocaleDateString()}`);
      await logout();
      navigate('/auth', { replace: true });
    } catch {
      toast.error('Could not deactivate');
      setDeactivating(false);
    }
  };

  const doDelete = async () => {
    if (confirmDelete.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setDeleting(true);
    try {
      await api.post('/me/delete');
      toast.success('Account deleted');
      await logout();
      navigate('/auth', { replace: true });
    } catch {
      toast.error('Could not delete');
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="deactivate-page">
      <header className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="deact-back" aria-label="Back"
          className="w-9 h-9 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#4B4B52]">
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">ACCOUNT</p>
          <h1 className="font-brand text-[26px] font-[500] tracking-tight text-[#0F0F10] leading-tight">Deactivate</h1>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-5 space-y-6">
        {/* Deactivate (soft) */}
        <section className="rounded-2xl border border-[var(--hairline)] p-5 bg-white" data-testid="deact-soft-block">
          <div className="flex items-start gap-3 mb-3">
            <PauseCircle size={22} className="text-[#B38138] flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-brand text-[19px] font-[500] tracking-tight text-[#0F0F10] mb-1">Pause my account</h2>
              <p className="text-[13px] text-[#4B4B52] leading-relaxed">
                Take a break for up to <strong>30 days</strong>. Your profile disappears from Discover, Likes, and Matches. Sign back in any time to pick up where you left off — your matches and chats are frozen, not deleted.
              </p>
            </div>
          </div>
          <button
            onClick={doDeactivate}
            disabled={deactivating}
            data-testid="deact-submit-btn"
            className="w-full py-3 rounded-full bg-[#B38138] hover:bg-[#8f6529] text-white text-sm font-semibold disabled:opacity-50 transition"
          >
            {deactivating ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Deactivate for 30 days'}
          </button>
        </section>

        {/* Hard delete */}
        <section className="rounded-2xl border border-[#B91C1C]/30 p-5 bg-[#FEF2F2]" data-testid="deact-hard-block">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle size={22} className="text-[#B91C1C] flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-brand text-[19px] font-[500] tracking-tight text-[#0F0F10] mb-1">Delete permanently</h2>
              <p className="text-[13px] text-[#4B4B52] leading-relaxed">
                Wipes your account, photos, matches, and messages immediately. <strong>This cannot be undone.</strong> If you might come back, use "Pause my account" above.
              </p>
            </div>
          </div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-[#B91C1C] block mb-1.5">Type DELETE to confirm</label>
          <input
            value={confirmDelete}
            onChange={(e) => setConfirmDelete(e.target.value)}
            placeholder="DELETE"
            data-testid="deact-confirm-input"
            className="w-full px-3 py-2.5 rounded-lg border border-[#B91C1C]/30 bg-white text-sm focus:outline-none focus:border-[#B91C1C] mb-3"
          />
          <button
            onClick={doDelete}
            disabled={deleting || confirmDelete.trim().toUpperCase() !== 'DELETE'}
            data-testid="deact-delete-btn"
            className="w-full py-3 rounded-full bg-[#B91C1C] hover:bg-[#8a1616] text-white text-sm font-semibold disabled:opacity-40 transition inline-flex items-center justify-center gap-2"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 size={14}/> Delete my account forever</>}
          </button>
        </section>
      </div>
    </div>
  );
}
